/**
 * "Add-on seat waste" worklist — purchased add-on seats minus per-user assigned, ranked by waste then
 * soonest renewal. Purpose-built (NOT the generic WorklistTable): the columns are add-on specific
 * (category, purchased, assigned, idle, utilization). Honours the same guards the view encodes: Phone is
 * PAYG (idle "—", a metered chip); an account with no per-user license coverage shows assigned/idle as
 * "—" (never a false "all wasted"). The renewal-amount money column is gated on canSeeMoney() AND the
 * server strips it for a non-money role — a worklist-but-no-money role gets the list without money.
 */

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Badge, Table, type Column, ErrorState, EmptyState, SkeletonRows } from "@/components/ui";
import { useRbac } from "@/context/RbacContext";
import { formatDate, formatInt, formatMoney } from "@/lib/format";
import type { AddonWasteRow } from "@/api/types";
import { UtilizationBar, utilizationTone, bandFromPct } from "@/features/customer/display";
import { asTKey, type TKey } from "@/i18n/keys";
import { useAddonWaste } from "./useWorklists";

// Deliberately NOT `ReturnType<typeof useTranslation>["t"]` for the `t` param below — see the identical
// note in SyncNowControl.tsx: that exact overloaded generic type gets expensive enough against a large
// resource tree that adding the Pool module's ~150 keys pushed this call over tsc's "type instantiation
// is excessively deep" limit. The call here passes a real key with no interpolation options, so this
// narrower structural alias is sufficient.
/** Add-on category token → a human label (i18n). Unknown tokens fall back to the raw token. */
function categoryLabel(t: (key: TKey) => string, cat: string): string {
  const key = asTKey("worklist.addonWaste.cat.", cat);
  return key ? t(key) : cat;
}

export function AddonWasteTable() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canSeeMoney } = useRbac();
  const wl = useAddonWaste();

  if (wl.isPending) return <SkeletonRows rows={5} />;
  if (wl.isError) return <ErrorState error={wl.error} onRetry={() => void wl.refetch()} />;

  const rows = wl.data.rows;
  const showAmount = canSeeMoney() && rows.some((r) => r.renewal_amount != null);

  const columns: Column<AddonWasteRow>[] = [
    {
      key: "account",
      header: t("worklist.col.account"),
      cell: (r) => (
        <span className="font-medium text-(--text)">
          {r.account_name ?? r.account_number ?? r.account_id}
        </span>
      ),
      sortValue: (r) => r.account_name ?? r.account_number ?? r.account_id,
    },
    {
      key: "addon",
      header: t("worklist.addonWaste.addon"),
      cell: (r) => (
        <span className="flex items-center gap-1.5">
          <span className="text-(--text)">{categoryLabel(t, r.addon_category)}</span>
          {r.is_payg && <Badge tone="info">{t("customer.usage.payg")}</Badge>}
        </span>
      ),
      sortValue: (r) => r.addon_category,
    },
    {
      key: "purchased",
      header: t("worklist.addonWaste.purchased"),
      cell: (r) => (r.purchased_seats == null ? "—" : formatInt(r.purchased_seats)),
      sortValue: (r) => r.purchased_seats,
      align: "right",
      numeric: true,
    },
    {
      key: "assigned",
      // Coverage-guarded: no per-user data → "—" (NOT a false "0").
      header: t("worklist.addonWaste.assigned"),
      cell: (r) =>
        !r.has_license_coverage || r.assigned_seats == null ? (
          <span className="text-(--text-subtle)">—</span>
        ) : (
          formatInt(r.assigned_seats)
        ),
      sortValue: (r) => r.assigned_seats,
      align: "right",
      numeric: true,
    },
    {
      key: "waste",
      header: t("worklist.addonWaste.waste"),
      cell: (r) =>
        r.is_payg ? (
          <Badge tone="info">{t("customer.usage.payg")}</Badge>
        ) : r.waste_seats == null ? (
          <span className="text-(--text-subtle)">—</span>
        ) : r.waste_seats === 0 ? (
          <Badge tone="ok">{t("customer.usage.fullyUsed")}</Badge>
        ) : (
          <Badge tone="warn">{t("customer.usage.idleSeats", { count: r.waste_seats })}</Badge>
        ),
      sortValue: (r) => r.waste_seats,
      align: "right",
    },
    {
      key: "util",
      header: t("customer.utilization"),
      cell: (r) =>
        r.utilization_pct == null ? (
          <span className="text-(--text-subtle)">—</span>
        ) : (
          <UtilizationBar
            pct={r.utilization_pct}
            tone={utilizationTone(bandFromPct(r.utilization_pct))}
            className="w-32"
          />
        ),
      sortValue: (r) => r.utilization_pct,
    },
    {
      key: "next",
      header: t("customer.subscription.nextInvoice"),
      cell: (r) => formatDate(r.next_invoice_date),
      sortValue: (r) => r.next_invoice_date,
    },
    ...(showAmount
      ? [
          {
            key: "amount",
            header: t("customer.money.renewalAmount"),
            cell: (r: AddonWasteRow) =>
              formatMoney(r.renewal_amount ?? null, r.renewal_currency ?? null) ?? "—",
            sortValue: (r: AddonWasteRow) => r.renewal_amount ?? null,
            align: "right" as const,
            numeric: true,
          } satisfies Column<AddonWasteRow>,
        ]
      : []),
  ];

  return (
    <Table
      columns={columns}
      rows={rows}
      rowKey={(r) => `${r.account_id}:${r.addon_category}`}
      caption={t("worklist.addonWaste.title")}
      initialSort={{ key: "waste", dir: "desc" }}
      onRowClick={(r) => navigate(`/customers/${encodeURIComponent(r.account_id)}`)}
      empty={<EmptyState title={t("worklist.empty")} body={t("worklist.addonWaste.emptyBody")} />}
    />
  );
}
